import { type ChartTone, signTone, TONE_COLOR } from './chart-colors.js';
import { cn } from '../../utils/cn.js';

export interface CategoryBar {
  key: string;
  label: string;
  value: number;
  /** Default: the value's sign (gain green, loss red). Colours the bar and its printed value. */
  tone?: ChartTone;
  /** Printed beside the bar (default: the value to 2 decimals). */
  display?: string;
}

interface CategoryBarsProps {
  bars: readonly CategoryBar[];
  title: string;
}

/** Where a bar sits on a 0–100 % track: from the left edge, or around the centre when any value is negative. */
export function barGeometry(value: number, max: number, centred: boolean): { left: number; width: number } {
  const share = max > 0 ? Math.abs(value) / max : 0;
  if (!centred) return { left: 0, width: share * 100 };
  return value < 0 ? { left: 50 - share * 50, width: share * 50 } : { left: 50, width: share * 50 };
}

/** The printed value's colour: the bar's own tone when it has one (a drawdown row is red), else the value's sign. */
function valueClass(bar: CategoryBar): string {
  const tone = bar.tone ?? signTone(bar.value);
  return tone === 'danger'
    ? 'text-bear'
    : tone === 'success'
      ? 'text-bull'
      : tone === 'muted'
        ? 'text-muted'
        : 'text-heading';
}

/**
 * One signed value per category as a bar (NextAdmin traffic-sources list): P&L by side, by signal, by weekday. Gains
 * grow right and losses left of a centre line; the value is printed beside each bar.
 */
export function CategoryBars({ bars, title }: CategoryBarsProps) {
  const max = Math.max(1e-12, ...bars.map((bar) => Math.abs(bar.value)));
  const centred = bars.some((bar) => bar.value < 0);
  return (
    <ul aria-label={title} className="space-y-3">
      {bars.map((bar) => {
        const { left, width } = barGeometry(bar.value, max, centred);
        return (
          <li
            key={bar.key}
            className="grid grid-cols-[minmax(4rem,8rem)_1fr] items-center gap-x-3 gap-y-1 text-sm sm:grid-cols-[minmax(4rem,9rem)_1fr_auto]"
          >
            <span className="truncate text-text-secondary" title={bar.label}>
              {bar.label}
            </span>
            <div className="relative h-2 overflow-hidden rounded-full bg-surface-alt" aria-hidden="true">
              {centred && <span className="absolute inset-y-0 left-1/2 w-px bg-faint" />}
              <span
                className="absolute inset-y-0 rounded-full"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  backgroundColor: TONE_COLOR[bar.tone ?? signTone(bar.value)],
                }}
              />
            </div>
            <span className={cn('col-span-2 font-medium tabular-nums sm:col-span-1 sm:text-right', valueClass(bar))}>
              {bar.display ?? bar.value.toFixed(2)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
